import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import chunk from 'lodash/chunk';
import React from 'react';

import { GET_MEDIAS_BY_IDS } from 'Apollo/Store/Media/queries';
import { updateShowroomMediaParams, updateShowroomSectionLoaded, updateShowroomSectionLoading } from 'Apollo/Store/Showroom/Editor/resolvers';
import {
	GetMediasByIdsQuery,
	useGetPublicShowroomWithoutSectionsQuery,
	useGetShowroomBasketInfoQuery,
	useGetShowroomDownloadEnabledQuery,
	useGetShowroomSectionLoadingMediaFieldsQuery,
	useGetShowroomWithoutSectionsQuery
} from 'generated/graphql';

import { ShowroomFilterTree } from 'Types/showroom';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

const FETCH_ROWS_BATCH_SIZE = 10;

// The following hooks are utility functions to reuse the fetching of different informations inside the showroom components. Most of them are relying on the current showroom id state
// NOTE: These hooks were made to avoid writing the same code in different showroom components. If a component needs a super specific bunch of data. It's ok to use the useQuery method directly in the component
export const useIsShowroomMobileVersion = () => {
	const theme = useTheme();
	return useMediaQuery(theme.breakpoints.down('md'));
};

export const useGetCurrentShowroomId = () => {
	const { showroomId } = React.useContext(ShowroomContext);
	return showroomId;
};

const useGetShowroomData = () => {
	const showroomId = useGetCurrentShowroomId();
	const { data } = useGetShowroomWithoutSectionsQuery({ variables: { id: showroomId }, fetchPolicy: 'cache-only' });
	return data?.getShowroom;
};

const useGetPulblicShowroomData = () => {
	const showroomId = useGetCurrentShowroomId();
	const { data } = useGetPublicShowroomWithoutSectionsQuery({ variables: { id: showroomId }, fetchPolicy: 'cache-only' });
	return data?.getShowroom;
};

export const useGetCurrentShowroomData = () => {
	const { isPublic } = React.useContext(ShowroomContext);
	const showroomData = useGetShowroomData();
	const showroomPublicData = useGetPulblicShowroomData();
	return isPublic ? showroomPublicData : showroomData;
};

export const useIsShowroomUserView = () => {
	const { isUserView } = React.useContext(ShowroomContext);
	return isUserView;
};

export const useIsShowroomDowloadOptionEnabled = () => {
	const showroomId = useGetCurrentShowroomId();
	const { data } = useGetShowroomDownloadEnabledQuery({ variables: { showroomId }, fetchPolicy: 'cache-only' });
	return data.getShowroom.downloadEnabled;
};

export const useIsShowroomBasketEnabled = () => {
	const showroomId = useGetCurrentShowroomId();
	const { data } = useGetShowroomBasketInfoQuery({ variables: { showroomId }, fetchPolicy: 'cache-only' });
	const { getShowroom: showroomBasketInfo } = data;
	const displayBasket = React.useMemo(
		() => showroomBasketInfo.tenant.isSamplesEnabled && showroomBasketInfo.requestSamplesEnabled && showroomBasketInfo.hasSamples,
		[showroomBasketInfo]
	);
	return displayBasket;
};

export const useGetShowroomMediasByIds = (sectionId: string) => {
	const client = useApolloClient();

	const {
		data: {
			showroomSection: { loaded, loading, rowMediaIds }
		}
	} = useGetShowroomSectionLoadingMediaFieldsQuery({
		variables: {
			id: sectionId
		}
	});

	React.useEffect(() => {
		if (loaded) return;

		let active = true;
		const fetchMedias = async () => {
			const mediaIds = rowMediaIds.reduce((acc, row) => {
				acc.push(...row.mediaIds);
				return acc;
			}, []);
			const mediaChunks = chunk(mediaIds, FETCH_ROWS_BATCH_SIZE);
			const promises: Promise<ApolloQueryResult<GetMediasByIdsQuery>>[] = mediaChunks.map(mediaIds => {
				return client.query({
					query: GET_MEDIAS_BY_IDS,
					variables: {
						mediaIds: mediaIds
					},
					fetchPolicy: 'cache-first'
				});
			});
			const responses = await Promise.all(promises);

			if (!active) return;

			const medias = responses.flatMap(res => res.data.mediasByIds);
			updateShowroomSectionLoaded(sectionId, true);
			updateShowroomMediaParams(sectionId, medias);
			updateShowroomSectionLoading(sectionId, false);
		};

		fetchMedias();

		return () => {
			active = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client, loaded, sectionId]);

	return {
		loading,
		loaded
	};
};

export const useGetShowroomScrollElement = () => {
	const { scrollElement } = React.useContext(ShowroomContext);
	return scrollElement || window;
};

const filtersSort = ['brand', 'season', 'collection', 'accessories', 'clothes', 'makeup', 'hairstyle', 'color', 'fabric'];

export const useSortShowroomFilterTrees = (filterTrees: ShowroomFilterTree[]) => {
	const sorted = React.useMemo(
		() =>
			[...filterTrees].sort((value1, value2) => {
				const getFilterSortPosition = (value: ShowroomFilterTree) => {
					const index = filtersSort.indexOf(value.name);
					return index === -1 ? Number.MAX_SAFE_INTEGER : index;
				};
				const value1Pos = getFilterSortPosition(value1);
				const value2Pos = getFilterSortPosition(value2);
				if (value1Pos < value2Pos) return -1;
				else if (value1Pos === value2Pos) return 0;
				else return 1;
			}),
		[filterTrees]
	);

	return { sorted };
};
